import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Button, ButtonLink } from "../ui";

interface Props {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Можно отправить в Sentry и т.п.
    console.error("[ErrorBoundary]", error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      if (fallback) return fallback(error, this.reset);
      return (
        <div role="alert" className="p-6 m-4 rounded-lg border border-danger-line bg-danger-soft text-danger-ink">
          <h2 className="text-lg font-semibold mb-2">Что-то пошло не так</h2>
          <p className="text-sm mb-3">Сообщите разработчику, если это повторяется.</p>
          <pre className="text-xs whitespace-pre-wrap break-all bg-surface p-2 rounded border border-danger-line mb-3">
            {error.name}: {error.message}
          </pre>
          <Button variant="primary" onClick={this.reset}>
            Попробовать снова
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Граница вокруг содержимого страницы — внутри каркаса, а не снаружи.
 *
 *  Внешняя граница в main.tsx ловит всё, но и прячет всё: при ошибке в одной
 *  таблице исчезали меню, шапка и колокольчик, и человек оставался перед
 *  сообщением без выхода. Здесь падает только страница; меню остаётся, по
 *  нему можно уйти. Ключ по адресу: переход на другую страницу сбрасывает
 *  ошибку сам, без «Попробовать снова».
 */
export function PageErrorBoundary({ children, home = "/" }: { children: ReactNode; home?: string }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary
      key={pathname}
      fallback={(error, reset) => (
        <div
          role="alert"
          className="mx-auto my-6 max-w-2xl rounded-lg border border-danger-line bg-danger-soft p-6 text-danger-ink"
        >
          <h2 className="mb-2 text-lg font-semibold">Страница не открылась</h2>
          <p className="mb-3 text-sm">
            Ошибка в интерфейсе, данные целы. Попробуйте ещё раз или перейдите в другой раздел через меню;
            если повторяется — сообщите разработчику.
          </p>
          <pre className="mb-3 whitespace-pre-wrap break-all rounded border border-danger-line bg-surface p-2 text-xs">
            {error.name}: {error.message}
          </pre>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={reset}>
              Попробовать снова
            </Button>
            <ButtonLink to={home} variant="secondary">
              На главную
            </ButtonLink>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
